/**
 * Helpers for persisted v2 iniciarpago/broadcastpago flows.
 */

export const PAYMENT_FLOW_CLASSIC = 'classic';
export const PAYMENT_FLOW_VOLUME = 'volume';

const PAYMENT_BROADCAST_SUCCESS_STATUS_BY_KIND = {
    NORMAL_TRANSFER: 'BROADCASTED',
    DIRECT_FUND: 'FUNDING_SENT',
    DIRECT_PULL: 'PULL_SENT',
};

export const PAYMENT_FLOW_V2_REQUIRED_MESSAGE =
    'El pago solo usa flujo v2 (iniciarpago -> firmar -> broadcastpago). ' +
    'iniciarpago debe devolver una fila con transactionDetails por cada leg esperado ' +
    '(2 por consumo directo clasico y 1 por volumen). No se envian transacciones a la blockchain desde el navegador.';

export const getExpectedKindsForTransaction = (transaction, flowType = PAYMENT_FLOW_CLASSIC) => {
    if (flowType === PAYMENT_FLOW_VOLUME) {
        return ['NORMAL_TRANSFER'];
    }
    if (transaction?.directPayment) {
        return ['DIRECT_FUND', 'DIRECT_PULL'];
    }
    return ['NORMAL_TRANSFER'];
};

export const iniciarLegCountForTransaction = (transaction, flowType = PAYMENT_FLOW_CLASSIC) =>
    getExpectedKindsForTransaction(transaction, flowType).length;

export const expectedIniciarItemCount = (transactions, flowType = PAYMENT_FLOW_CLASSIC) =>
    (transactions || []).reduce(
        (sum, transaction) => sum + iniciarLegCountForTransaction(transaction, flowType),
        0,
    );

export const paymentFlowV2IniciarMatchesSelection = (
    iniciarItems,
    transactions,
    flowType = PAYMENT_FLOW_CLASSIC,
) => {
    return validateIniciarPagoResponse(iniciarItems, transactions, flowType).ok;
};

/** Backend v2 must return an array of { id, kind, transactionDetails }. */
export const normalizeIniciarPagoResponse = (response) => {
    if (Array.isArray(response)) {
        return response;
    }
    if (response && Array.isArray(response.results)) {
        return response.results;
    }
    if (response && Array.isArray(response.iniciarItems)) {
        return response.iniciarItems;
    }
    return null;
};

const hasExplicitTransactionId = (value) => value !== undefined && value !== null && value !== '';

const getResponseTransactionIdentity = (row, flowType = PAYMENT_FLOW_CLASSIC) => {
    if (flowType === PAYMENT_FLOW_VOLUME) {
        if (hasExplicitTransactionId(row?.registro_consumo_id)) {
            return {
                error: `iniciarpago devolvio una fila Classic para un pago Volume (id ${row?.registro_consumo_id}).`,
            };
        }
        if (hasExplicitTransactionId(row?.registro_consumo_volumen_id)) {
            return { id: row.registro_consumo_volumen_id };
        }
        return { id: row?.id };
    }

    if (hasExplicitTransactionId(row?.registro_consumo_volumen_id)) {
        return {
            error: `iniciarpago devolvio una fila Volume para un pago Classic (id ${row?.registro_consumo_volumen_id}).`,
        };
    }
    if (hasExplicitTransactionId(row?.registro_consumo_id)) {
        return { id: row.registro_consumo_id };
    }
    return { id: row?.id };
};

const getResponseTransactionId = (row) =>
    row?.registro_consumo_id ?? row?.registro_consumo_volumen_id ?? row?.id;

const sameTransactionId = (left, right) => String(left) === String(right);

export const validateIniciarPagoResponse = (
    response,
    transactions,
    flowType = PAYMENT_FLOW_CLASSIC,
) => {
    const normalized = normalizeIniciarPagoResponse(response);
    if (!normalized?.length) {
        return {
            ok: false,
            error: 'iniciarpago no devolvio filas con transactionDetails (respuesta vacia o backend sin v2).',
        };
    }
    if (normalized.some((row) => !row?.transactionDetails)) {
        return {
            ok: false,
            error: 'iniciarpago devolvio filas sin transactionDetails.',
        };
    }

    const expected = expectedIniciarItemCount(transactions, flowType);
    if (normalized.length !== expected) {
        return {
            ok: false,
            error: `iniciarpago devolvio ${normalized.length} fila(s); se esperaban ${expected}.`,
        };
    }

    const normalizedRows = [];
    for (const row of normalized) {
        const identity = getResponseTransactionIdentity(row, flowType);
        if (identity.error) {
            return {
                ok: false,
                error: identity.error,
            };
        }
        normalizedRows.push({
            row,
            transactionId: identity.id,
        });
    }

    for (const transaction of transactions || []) {
        const expectedKinds = getExpectedKindsForTransaction(transaction, flowType);
        const rows = normalizedRows
            .filter(({ transactionId }) => sameTransactionId(transactionId, transaction.id))
            .map(({ row }) => row);
        if (rows.length !== expectedKinds.length) {
            return {
                ok: false,
                error: `iniciarpago devolvio ${rows.length} leg(s) para la transaccion ${transaction.id}; se esperaban ${expectedKinds.length}.`,
            };
        }
        const actualKinds = rows.map((row) => row?.kind);
        if (actualKinds.some((kind) => !kind)) {
            return {
                ok: false,
                error: `iniciarpago devolvio filas sin kind para la transaccion ${transaction.id}.`,
            };
        }
        if (actualKinds.join('|') !== expectedKinds.join('|')) {
            return {
                ok: false,
                error: `iniciarpago devolvio legs incompatibles para la transaccion ${
                    transaction.id
                }: ${actualKinds.join(', ')}.`,
            };
        }
    }

    return { ok: true, items: normalized };
};

/**
 * Body for PUT iniciarpago. pago_directo when OS row has directPayment (from API / obra_social.pago_directo).
 */
export const buildIniciarPagoRequestBody = (transactionIds, transactionRows = []) =>
    transactionIds.map((tId) => {
        const row = transactionRows.find((transaction) => String(transaction.id) === String(tId));
        const body = { id: parseFloat(tId) };
        if (row?.directPayment) {
            body.pago_directo = true;
        }
        return body;
    });

export const resolveTransactionsForIniciarValidation = (transactionIds, rowsFromState) => {
    return transactionIds.map((id) => {
        const row = rowsFromState.find((transaction) => String(transaction.id) === String(id));
        return row || { id: parseFloat(id), directPayment: false };
    });
};

export const buildBroadcastPayloadItem = (item, signedTransaction) => ({
    id: item.id,
    kind: item.kind,
    signedTransaction,
    transactionDetails: item.transactionDetails,
});

export const normalizeBroadcastResults = (response) => {
    if (Array.isArray(response)) {
        return response;
    }
    if (response && Array.isArray(response.results)) {
        return response.results;
    }
    return [];
};

export const summarizeBroadcastResultsByTransaction = (
    broadcastResults,
    transactions,
    flowType = PAYMENT_FLOW_CLASSIC,
) =>
    (transactions || []).map((transaction) => {
        const expectedKinds = getExpectedKindsForTransaction(transaction, flowType);
        const rows = normalizeBroadcastResults(broadcastResults).filter((row) =>
            sameTransactionId(getResponseTransactionId(row), transaction.id),
        );
        const errors = [];

        for (const kind of expectedKinds) {
            const matchedRows = rows.filter((row) => row?.kind === kind);
            if (matchedRows.length !== 1) {
                errors.push(`El backend devolvio ${matchedRows.length} resultado(s) para ${kind}.`);
                continue;
            }

            const matchedRow = matchedRows[0];
            if (matchedRow?.error) {
                errors.push(String(matchedRow.error));
                continue;
            }
            if (matchedRow?.errores?.length) {
                matchedRow.errores.forEach((error) =>
                    errors.push(error?.description || error?.detail || String(error)),
                );
                continue;
            }

            const expectedStatus = PAYMENT_BROADCAST_SUCCESS_STATUS_BY_KIND[kind];
            if (matchedRow?.status !== expectedStatus) {
                errors.push(
                    `Estado inesperado para ${kind}: ${
                        matchedRow?.status || 'SIN_ESTADO'
                    } (esperado ${expectedStatus}).`,
                );
            }
        }

        const unexpectedKinds = rows
            .map((row) => row?.kind)
            .filter((kind) => kind && !expectedKinds.includes(kind));
        if (unexpectedKinds.length) {
            errors.push(`El backend devolvio legs inesperados: ${unexpectedKinds.join(', ')}.`);
        }

        if (!rows.length) {
            errors.push('El backend no devolvio resultados para la transaccion.');
        }

        return {
            id: transaction.id,
            rows,
            expectedKinds,
            ok: errors.length === 0,
            errors,
        };
    });

export const buildPaymentResultsForUi = (
    broadcastResults,
    transactions,
    flowType = PAYMENT_FLOW_CLASSIC,
) =>
    summarizeBroadcastResultsByTransaction(broadcastResults, transactions, flowType).map(
        (summary) => ({
            id: summary.id,
            errors: summary.errors.map((description) => ({ description })),
        }),
    );

export const extractBroadcastAttemptedTransactionIds = (broadcastResults) => {
    const attemptedIds = new Set();
    normalizeBroadcastResults(broadcastResults).forEach((row) => {
        const rowId = getResponseTransactionId(row);
        if (rowId !== undefined && rowId !== null && rowId !== '') {
            attemptedIds.add(String(rowId));
        }
    });
    return attemptedIds;
};

export const getResettableTransactionIdsAfterBroadcastFailure = (
    selectedTransactionIds,
    broadcastResults,
) => {
    const normalizedSelectedIds = new Set((selectedTransactionIds || []).map((id) => String(id)));
    const attemptedIds = extractBroadcastAttemptedTransactionIds(broadcastResults);
    const matchingAttemptedIds = new Set(
        [...attemptedIds].filter((id) => normalizedSelectedIds.has(id)),
    );

    if (matchingAttemptedIds.size === 0) {
        return [];
    }

    return (selectedTransactionIds || []).filter((id) => !matchingAttemptedIds.has(String(id)));
};
